import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { CopyButton } from "../lib/CopyButton";
import { countdown, formatSgNumber } from "../lib/format";
import { SignupProgress } from "../lib/signupProgress";
import { CheckIcon, ClockIcon } from "../lib/ui";
import { WaQr } from "../lib/waQr";

export interface SignupForm {
  name: string;
  email: string;
  proxyType: "mobile" | "uen";
  proxyValue: string;
  refPrefix: string;
}

interface Started {
  pollToken: string;
  code: string;
  message: string;
  link: string;
  whatsappNumber: string;
  expiresAt: string;
  number: string;
}

type Status =
  | { state: "waiting"; secondsLeft: number }
  | { state: "expired" }
  | { state: "wrong_number"; secondsLeft: number; mismatch: { from: string; at: string; canAdopt: boolean } }
  | { state: "verified"; number: string; proof: string };

type View =
  | { kind: "starting" }
  | { kind: "waiting" }
  | { kind: "wrong"; from: string; at: string; canAdopt: boolean }
  | { kind: "expired" }
  | { kind: "toomany"; until: number }
  | { kind: "confirmed"; number: string; proof: string; at: Date }
  | { kind: "error"; message: string };

const WHATSAPP_DOWNLOAD = "https://www.whatsapp.com/download";

/**
 * Sign-up, confirming a PayNow mobile number on WhatsApp ("reverse OTP"): the person sends
 * ConfirmPay a code from the phone, instead of ConfirmPay texting one. Asks for the code, shows
 * how to send it, and polls every couple of seconds for whether it arrived and from which number.
 */
export function PhoneStep({
  form,
  emailProof,
  onChangeNumber,
  onByHand,
  onContinue,
}: {
  form: SignupForm;
  emailProof: string;
  onChangeNumber: () => void;
  onByHand: () => void;
  onContinue: (c: { proof: string; number: string }) => void;
}) {
  const [session, setSession] = useState<Started | null>(null);
  const [view, setView] = useState<View>({ kind: "starting" });
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [adoptError, setAdoptError] = useState("");
  const [noApp, setNoApp] = useState(false);
  const dismissed = useRef<string | null>(null);
  const started = useRef(false);
  const tapTimer = useRef<number | undefined>(undefined);

  const start = useCallback(async () => {
    setView({ kind: "starting" });
    setSession(null);
    setNoApp(false);
    setAdoptError("");
    dismissed.current = null;
    const res = await api.post<Started>("/api/onboarding/phone/start", { ...form, emailProof });
    if (!res.ok) {
      if (res.status === 429) {
        setView({ kind: "toomany", until: Date.now() + (res.retryAfter ?? 600) * 1000 });
      } else if (res.body?.unavailable) {
        onByHand();
      } else {
        setView({ kind: "error", message: res.error });
      }
      return;
    }
    setSession(res.data);
    setView({ kind: "waiting" });
  }, [form, emailProof, onByHand]);

  // One code per visit: React's dev double-run of effects must not spend two.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start();
  }, [start]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // "Has it arrived?" while a code is live.
  useEffect(() => {
    if (!session || (view.kind !== "waiting" && view.kind !== "wrong")) return;
    let stop = false;
    const poll = async () => {
      const res = await api.get<Status>(`/api/onboarding/phone/status?token=${session.pollToken}`);
      if (stop || !res.ok) return;
      const s = res.data;
      if (s.state === "verified") {
        setView({ kind: "confirmed", number: s.number, proof: s.proof, at: new Date() });
      } else if (s.state === "expired") {
        setView({ kind: "expired" });
      } else if (s.state === "wrong_number" && s.mismatch.at !== dismissed.current) {
        setView({ kind: "wrong", from: s.mismatch.from, at: s.mismatch.at, canAdopt: s.mismatch.canAdopt });
      }
    };
    void poll();
    const t = window.setInterval(poll, 2500);
    return () => {
      stop = true;
      window.clearInterval(t);
    };
  }, [session, view.kind]);

  // The clock reaching zero ends the code even before the next poll says so.
  const secondsLeft = session ? Math.max(0, Math.floor((new Date(session.expiresAt).getTime() - now) / 1000)) : 0;
  useEffect(() => {
    if (view.kind === "waiting" && session && secondsLeft === 0) setView({ kind: "expired" });
  }, [view.kind, session, secondsLeft]);

  // On a phone, a tap that leaves this page open means WhatsApp didn't open.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") {
        window.clearTimeout(tapTimer.current);
        setNoApp(false);
      }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.clearTimeout(tapTimer.current);
    };
  }, []);

  function onOpenWhatsApp() {
    window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      if (document.visibilityState === "visible") setNoApp(true);
    }, 3000);
  }

  async function adopt() {
    if (!session) return;
    setBusy(true);
    setAdoptError("");
    const res = await api.post<{ number: string; proof: string }>("/api/onboarding/phone/adopt", { token: session.pollToken });
    setBusy(false);
    if (!res.ok) {
      setAdoptError(res.error);
      return;
    }
    setView({ kind: "confirmed", number: res.data.number, proof: res.data.proof, at: new Date() });
  }

  const typed = formatSgNumber(form.proxyValue.replace(/\D/g, "").replace(/^(\d{8})$/, "65$1"));
  const heading = (text: string) => (
    <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>{text}</h2>
  );
  const progress = (done = false) => (
    <SignupProgress states={done ? ["done", "done", "done"] : ["done", "done", "cur"]} />
  );

  if (view.kind === "starting") {
    return (
      <>
        {progress()}
        {heading("Confirm your PayNow number")}
        <div className="note note--wait" role="status" style={{ marginTop: 28 }}>
          <span className="spin" /> Getting your code…
        </div>
      </>
    );
  }

  if (view.kind === "error") {
    return (
      <>
        {progress()}
        {heading("We couldn’t get a code")}
        <div className="error-box" role="alert" style={{ marginTop: 24 }}>{view.message}</div>
        <div className="pv-stack">
          <button type="button" className="btn btn--block" onClick={() => void start()}>Try again</button>
        </div>
        <div className="pv-links">
          <button type="button" className="textbtn" onClick={onChangeNumber}>Change number</button>
          <button type="button" className="textbtn" onClick={onByHand}>Can’t use WhatsApp?</button>
        </div>
      </>
    );
  }

  if (view.kind === "toomany") {
    const left = Math.max(0, Math.ceil((view.until - now) / 1000));
    return (
      <>
        {progress()}
        {heading("Too many codes for now")}
        <p className="body" style={{ margin: "12px 0 0", color: "var(--ink2)" }}>To keep sign-ups safe, we allow five codes an hour.</p>
        <div className="pv-alert" role="status">
          <ClockIcon size={30} strokeWidth={1.8} />
          <div>
            <div className="pv-count">{countdown(left)}</div>
            <div style={{ marginTop: 6, fontSize: 15, fontWeight: 600 }}>
              {left > 0 ? "until you can ask for another code" : "You can ask for a new code now"}
            </div>
          </div>
        </div>
        <div className="pv-stack">
          {left === 0 && <button type="button" className="btn btn--block" onClick={() => void start()}>Get a new code</button>}
          <button type="button" className="btn btn--ghost btn--block" onClick={onByHand}>Ask us to check it by hand</button>
          <p className="hint" style={{ margin: 0 }}>Your organisation is set up now, and payments switch on once we have approved it.</p>
        </div>
        <div className="pv-links">
          <button type="button" className="textbtn" onClick={onChangeNumber}>Back to details</button>
        </div>
      </>
    );
  }

  if (view.kind === "confirmed") {
    const at = view.at.toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit", hour12: false });
    return (
      <>
        {progress(true)}
        <div className="pv-done">
          <span className="pv-check"><CheckIcon size={30} strokeWidth={2.6} /></span>
          <div>
            {heading("Number confirmed")}
            <p className="body" style={{ margin: "8px 0 0", color: "var(--ink2)" }}>
              We got your message from <b className="mono" style={{ color: "var(--ink)", fontSize: 15 }}>{formatSgNumber(view.number)}</b> at {at}.
            </p>
          </div>
        </div>
        <div className="pv-box">
          <div className="pv-row">
            <div><div className="pv-k">PayNow mobile number</div><div className="pv-v">{formatSgNumber(view.number)}</div></div>
            <span className="pv-tag">Confirmed on WhatsApp</span>
          </div>
          <div className="pv-row">
            <div><div className="pv-k">Contact email</div><div className="pv-v">{form.email}</div></div>
            <span className="pv-tag">Confirmed by code</span>
          </div>
        </div>
        <div className="pv-next">
          <p className="hint" style={{ margin: 0, maxWidth: 300 }}>Next: switch on your bank’s payment emails, so ConfirmPay can see when money lands.</p>
          <button type="button" className="btn" onClick={() => onContinue({ proof: view.proof, number: view.number })}>Continue</button>
        </div>
      </>
    );
  }

  if (view.kind === "expired") {
    return (
      <>
        {progress()}
        {heading("That code has expired")}
        <p className="body" style={{ margin: "12px 0 0", color: "var(--ink2)" }}>Codes last 10 minutes, so an old message can’t be reused by someone else. Your details are saved.</p>
        <div style={{ marginTop: 30 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className="pv-label" style={{ margin: 0 }}>The old message</span>
            <span className="pv-tag pv-tag--wait" style={{ fontSize: 12.5, padding: "3px 10px" }}>Expired</span>
          </div>
          <span className="pv-stale">ConfirmPay code <b>{session?.code ?? ""}</b></span>
        </div>
        <div className="pv-stack" style={{ marginTop: 34 }}>
          <button type="button" className="btn btn--block" onClick={() => void start()}>Get a new code</button>
          <p className="hint" style={{ margin: 0 }}>A new code opens WhatsApp with a fresh message for <span className="mono">{typed}</span>.</p>
        </div>
        <div className="pv-links">
          <button type="button" className="textbtn" onClick={onChangeNumber}>Change number</button>
          <button type="button" className="textbtn" onClick={onByHand}>Can’t use WhatsApp?</button>
        </div>
      </>
    );
  }

  if (view.kind === "wrong") {
    const other = formatSgNumber(view.from);
    return (
      <>
        {progress()}
        {heading("That message came from a different number")}
        <p className="body" style={{ margin: "12px 0 0", color: "var(--ink2)" }}>We got your code, but not from the number you entered.</p>
        <div className="pv-box" style={{ marginTop: 26 }}>
          <div className="pv-row"><span className="pv-k">You entered</span><span className="pv-v">{typed}</span></div>
          <div className="pv-row pv-row--warn"><span className="pv-k">Message came from</span><span className="pv-v">{other}</span></div>
        </div>
        <div className="pv-stack">
          <button
            type="button"
            className="btn btn--block"
            onClick={() => {
              dismissed.current = view.at;
              setView({ kind: "waiting" });
            }}
          >
            Send it again from {typed}
          </button>
          {view.canAdopt && (
            <>
              <button type="button" className="btn btn--ghost btn--block" disabled={busy} onClick={() => void adopt()}>
                {busy ? "Checking…" : `My PayNow number is ${other}`}
              </button>
              <p className="hint" style={{ margin: 0 }}>
                Choose the second one if {other} is the number your PayNow is linked to. We will connect that number instead.
              </p>
            </>
          )}
          {!view.canAdopt && (
            <p className="hint" style={{ margin: 0 }}>{other} isn’t a Singapore mobile number, so it can’t be used for PayNow. Send the message again from {typed}.</p>
          )}
          {adoptError && <div className="error-box" role="alert">{adoptError}</div>}
        </div>
        <div className="pv-links">
          <button type="button" className="textbtn" onClick={onByHand}>Can’t use WhatsApp? Ask us to check it by hand</button>
        </div>
      </>
    );
  }

  // Default: waiting for the message.
  const s = session!;
  const waNumber = formatSgNumber(s.whatsappNumber);
  return (
    <>
      {progress()}
      {heading("Confirm your PayNow number")}
      <p className="body" style={{ margin: "12px 0 0", color: "var(--ink2)" }}>
        Send one WhatsApp message from <b className="mono" style={{ color: "var(--ink)", fontSize: 15 }}>{typed}</b>, the number you are connecting. That shows the number is yours. It costs nothing, and we only see this one message.
      </p>
      <p className="pv-sub"><b>Reverse OTP:</b> you send us the code, instead of us texting one to you.</p>

      <div className="pv-body">
        <div className="pv-steps">
          <div className="pv-desktop">
            <ol>
              <li><span className="pv-n">1</span><span>Scan the code with your phone’s camera.</span></li>
              <li><span className="pv-n">2</span><span>WhatsApp opens with the message ready. Press send, and leave the message as it is.</span></li>
              <li><span className="pv-n">3</span><span>Come back here. This page updates by itself.</span></li>
            </ol>
          </div>

          <div className="pv-mobile">
            <a className="btn btn--block" style={{ height: 56 }} href={s.link} target="_blank" rel="noopener noreferrer" onClick={onOpenWhatsApp}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 0 1-11.7 7.1L4 20l1-4.1A8 8 0 1 1 20 12z" /></svg>
              Open WhatsApp
            </a>
            {noApp && (
              <div style={{ marginTop: 16 }}>
                <div className="note note--wait" role="status" style={{ marginTop: 0 }}>
                  <span><b>Nothing opened?</b> WhatsApp may not be installed on this phone.</span>
                </div>
                <div className="pv-stack" style={{ marginTop: 14 }}>
                  <a className="btn btn--block" href={WHATSAPP_DOWNLOAD} target="_blank" rel="noopener noreferrer">Get WhatsApp</a>
                  <a className="btn btn--ghost btn--block" href={s.link} target="_blank" rel="noopener noreferrer" onClick={onOpenWhatsApp}>Try opening it again</a>
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 28 }}>
            <div className="pv-label">The message you will send</div>
            <span className="pv-bubble">ConfirmPay code <b>{s.code}</b></span>
          </div>
        </div>

        <div className="pv-qr">
          <div className="pv-qrtile"><WaQr text={s.link} label="QR code that opens WhatsApp with the message ready" /></div>
          <a className="textbtn" style={{ fontSize: 14, textAlign: "center" }} href={s.link} target="_blank" rel="noopener noreferrer">On this computer? Open WhatsApp Web</a>
        </div>
      </div>

      <div className="pv-mobile">
        <div className="pv-card">
          <h3>{noApp ? "Have WhatsApp on another device?" : "Prefer to send it yourself?"}</h3>
          {noApp && <p>Send this message from <b className="mono" style={{ color: "var(--ink)", fontSize: 13 }}>{typed}</b> there, and this page will still notice.</p>}
          <div className="pv-kv">
            <div><div className="pv-k">Send to</div><div className="pv-v">{waNumber}</div></div>
            <CopyButton text={`+${s.whatsappNumber}`} className="btn btn--ghost btn--sm">Copy</CopyButton>
          </div>
          <div className="pv-kv">
            <div><div className="pv-k">Message</div><div className="pv-v">{s.message}</div></div>
            <CopyButton text={s.message} className="btn btn--ghost btn--sm">Copy</CopyButton>
          </div>
        </div>
      </div>

      <div className="pv-status" role="status">
        <span className="pulse" aria-hidden="true" />
        <span>Waiting for your message from <span className="mono" style={{ fontSize: 14 }}>{typed}</span></span>
        <span className="pv-time">Code valid for {countdown(secondsLeft)}</span>
      </div>

      <div className="pv-links">
        <button type="button" className="textbtn" onClick={onChangeNumber}>Change number</button>
        <button type="button" className="textbtn" onClick={onByHand}>Can’t use WhatsApp?</button>
      </div>
    </>
  );
}
